import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Package, Factory, Cube, Gift, Truck, CheckCircle, MapPin, Clock, 
  Shield, ArrowLeft, Leaf, Plus, X, Eye, Coins, Trash, PencilSimple 
} from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = {
  raw_material: {
    icon: Package,
    label: 'Raw Material',
    color: '#4A6C4C',
    description: 'Sourcing of raw materials'
  },
  processing: {
    icon: Factory,
    label: 'Processing',
    color: '#C26D53',
    description: 'Material processing and preparation'
  },
  manufacturing: {
    icon: Cube,
    label: 'Manufacturing',
    color: '#D4A373',
    description: 'Product crafting and assembly'
  },
  packaging: {
    icon: Gift,
    label: 'Packaging',
    color: '#6B6659',
    description: 'Eco-friendly packaging'
  },
  distribution: {
    icon: Truck,
    label: 'Distribution',
    color: '#2C3E2D',
    description: 'Carbon-neutral delivery'
  }
};

const STAGE_ORDER = ['raw_material', 'processing', 'manufacturing', 'packaging', 'distribution'];

const SupplyChainPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddStage, setShowAddStage] = useState(false);
  const [addingStage, setAddingStage] = useState(false);

  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisName = localStorage.getItem('ginsgood_iris_name');

  const [newStage, setNewStage] = useState({
    stage: '',
    supplier_name: '',
    supplier_location: '',
    description: '',
    documents: '',
    images: ''
  });

  const fetchSupplyChain = useCallback(async () => {
    try {
      const [timelineRes, certRes] = await Promise.all([
        axios.get(`${API}/supply-chain/${certificateId}/timeline`),
        axios.get(`${API}/certificates/${certificateId}`)
      ]);
      setTimeline(timelineRes.data);
      setCertificate(certRes.data);
    } catch (error) {
      console.error('Error fetching supply chain:', error);
      toast.error('Failed to load supply chain data');
    } finally {
      setLoading(false);
    }
  }, [certificateId]);

  useEffect(() => {
    fetchSupplyChain();
  }, [fetchSupplyChain]);

  const handleAddStage = async (e) => {
    e.preventDefault();
    if (!newStage.stage || !newStage.supplier_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setAddingStage(true);
    try {
      const response = await axios.post(`${API}/supply-chain/add-stage`, null, {
        params: {
          certificate_id: certificateId,
          stage: newStage.stage,
          supplier_name: newStage.supplier_name,
          supplier_location: newStage.supplier_location,
          description: newStage.description,
          verified_by_iris: irisId,
          documents: newStage.documents ? newStage.documents.split(',').map(s => s.trim()) : [],
          images: newStage.images ? newStage.images.split(',').map(s => s.trim()) : []
        }
      });

      if (response.data.success) {
        toast.success(`Stage added! ${response.data.supply_chain_complete ? 'Supply chain complete! +150 GSG' : ''}`);
        setNewStage({ stage: '', supplier_name: '', supplier_location: '', description: '', documents: '', images: '' });
        setShowAddStage(false);
        fetchSupplyChain();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stage');
    } finally {
      setAddingStage(false);
    }
  };

  const handleVerifyStage = async (stageId) => {
    if (!irisId) {
      toast.error('Please register your identity first');
      navigate('/iris-auth');
      return;
    }

    try {
      const response = await axios.put(`${API}/supply-chain/stage/${stageId}/verify?verifier_iris_id=${irisId}`);
      if (response.data.success) {
        toast.success('Stage verified!');
        fetchSupplyChain();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify stage');
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!confirm('Are you sure you want to delete this stage?')) return;

    try {
      const response = await axios.delete(`${API}/supply-chain/stage/${stageId}`);
      if (response.data.success) {
        toast.success('Stage deleted');
        fetchSupplyChain();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete stage');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading supply chain...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const stages = timeline?.timeline || [];
  const summary = timeline?.summary || {};
  const canEdit = certificate?.seller_iris_id === irisId;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Button */}
          <Link to={`/passport/${certificateId}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Passport
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Leaf weight="duotone" className="w-4 h-4 mr-2" />
              Full Traceability
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Supply Chain Journey
            </h1>
            {timeline?.product && (
              <p className="text-muted-foreground">
                Tracing the journey of <span className="font-medium text-foreground">{timeline.product.product_name}</span>
              </p>
            )}
          </div>

          {/* Summary Card */}
          <Card className="p-6 mb-8 bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="font-medium mb-2">Transparency Progress</h3>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-primary">{summary.completion_percentage || 0}%</div>
                  <Badge variant={summary.is_complete ? "default" : "secondary"}>
                    {summary.is_complete ? 'Complete' : 'In Progress'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {summary.verified_stages || 0} of {summary.total_stages || 0} stages verified
                </p>
              </div>
              
              {summary.is_complete && (
                <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg">
                  <Coins className="w-5 h-5" />
                  <span className="font-medium">+150 GSG Earned</span>
                </div>
              )}
            </div>

            {/* Stage Progress Icons */}
            <div className="flex justify-between mt-6 pt-4 border-t border-primary/10">
              {STAGE_ORDER.map((stageKey, idx) => {
                const config = STAGE_CONFIG[stageKey];
                const Icon = config.icon;
                const isCompleted = summary.stages_covered?.includes(stageKey);
                
                return (
                  <div key={stageKey} className="flex flex-col items-center gap-2 relative">
                    {idx > 0 && (
                      <div className={`absolute top-5 -left-1/2 w-full h-0.5 ${isCompleted ? 'bg-primary' : 'bg-muted'}`} style={{ transform: 'translateX(-50%)' }} />
                    )}
                    <div 
                      className={`relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
                        isCompleted ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon weight={isCompleted ? "fill" : "regular"} className="w-5 h-5 md:w-6 md:h-6" />
                    </div>
                    <span className={`text-xs text-center hidden md:block ${isCompleted ? 'font-medium' : 'text-muted-foreground'}`}>
                      {config.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Add Stage Button */}
          {canEdit && (
            <div className="mb-8">
              <Button 
                onClick={() => setShowAddStage(!showAddStage)}
                variant={showAddStage ? "secondary" : "default"}
                className="gap-2"
                data-testid="add-stage-btn"
              >
                {showAddStage ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showAddStage ? 'Cancel' : 'Add Stage'}
              </Button>
            </div>
          )}

          {/* Add Stage Form */}
          {showAddStage && (
            <Card className="p-6 mb-8 border-2 border-primary" data-testid="add-stage-form">
              <h3 className="font-medium mb-4">Add Supply Chain Stage</h3>
              <form onSubmit={handleAddStage} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Stage Type *</Label>
                    <Select value={newStage.stage} onValueChange={(val) => setNewStage({ ...newStage, stage: val })}>
                      <SelectTrigger data-testid="stage-select">
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGE_ORDER.filter(s => !summary.stages_covered?.includes(s)).map(stageKey => (
                          <SelectItem key={stageKey} value={stageKey}>
                            {STAGE_CONFIG[stageKey].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Supplier Name *</Label>
                    <Input
                      value={newStage.supplier_name}
                      onChange={(e) => setNewStage({ ...newStage, supplier_name: e.target.value })}
                      placeholder="Supplier company name"
                      required
                      data-testid="supplier-name"
                    />
                  </div>
                </div>

                <div>
                  <Label>Supplier Location</Label>
                  <Input
                    value={newStage.supplier_location}
                    onChange={(e) => setNewStage({ ...newStage, supplier_location: e.target.value })}
                    placeholder="City, Country"
                    data-testid="supplier-location"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newStage.description}
                    onChange={(e) => setNewStage({ ...newStage, description: e.target.value })}
                    placeholder="Describe this stage of the supply chain..."
                    rows={3}
                    data-testid="stage-description"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Document URLs (comma-separated)</Label>
                    <Input
                      value={newStage.documents}
                      onChange={(e) => setNewStage({ ...newStage, documents: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Image URLs (comma-separated)</Label>
                    <Input
                      value={newStage.images}
                      onChange={(e) => setNewStage({ ...newStage, images: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <Button type="submit" disabled={addingStage} className="w-full" data-testid="submit-stage">
                  {addingStage ? 'Adding...' : 'Add Stage'}
                </Button>
              </form>
            </Card>
          )}

          {/* Timeline */}
          <div className="relative" data-testid="supply-chain-timeline">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />

            {stages.length === 0 ? (
              <Card className="p-8 ml-16 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No supply chain stages recorded yet.</p>
                {canEdit && (
                  <Button onClick={() => setShowAddStage(true)} variant="outline" className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add First Stage
                  </Button>
                )}
              </Card>
            ) : (
              <div className="space-y-6">
                {stages.map((stage, idx) => {
                  const config = STAGE_CONFIG[stage.stage_key] || STAGE_CONFIG.raw_material;
                  const Icon = config.icon;
                  
                  return (
                    <div key={stage.id} className="relative flex gap-6" data-testid={`stage-${stage.stage_key}`}>
                      {/* Icon */}
                      <div 
                        className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center shrink-0 shadow-md"
                        style={{ backgroundColor: config.color, color: 'white' }}
                      >
                        <Icon weight="fill" className="w-8 h-8" />
                      </div>

                      {/* Content */}
                      <Card className="flex-1 p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-lg">{stage.stage_label}</h3>
                              <Badge variant="outline" className="text-xs">Stage {stage.stage_order}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{stage.supplier_name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {stage.verified ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            ) : irisId ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleVerifyStage(stage.id)}
                                data-testid={`verify-${stage.id}`}
                              >
                                Verify
                              </Button>
                            ) : null}
                            {canEdit && (
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className="h-8 w-8 text-destructive"
                                onClick={() => handleDeleteStage(stage.id)}
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {stage.description && (
                          <p className="text-sm mb-4">{stage.description}</p>
                        )}

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {stage.supplier_location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {stage.supplier_location}
                            </div>
                          )}
                          {stage.created_at && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(stage.created_at).toLocaleDateString()}
                            </div>
                          )}
                          {stage.verified && stage.verifier_name && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Verified by {stage.verifier_name}
                            </div>
                          )}
                        </div>

                        {/* Documents & Images */}
                        {(stage.documents?.length > 0 || stage.images?.length > 0) && (
                          <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                            {stage.documents?.map((doc, i) => (
                              <a key={i} href={doc} target="_blank" rel="noopener noreferrer">
                                <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Document {i + 1}
                                </Badge>
                              </a>
                            ))}
                            {stage.images?.map((img, i) => (
                              <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                                <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Image {i + 1}
                                </Badge>
                              </a>
                            ))}
                          </div>
                        )}

                        {stage.blockchain_hash && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Shield className="w-4 h-4" />
                              <span>Blockchain: </span>
                              <code className="bg-muted px-2 py-1 rounded font-mono">
                                {stage.blockchain_hash.slice(0, 24)}...
                              </code>
                            </div>
                          </div>
                        )}
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Missing Stages Info */}
          {summary.missing_stages?.length > 0 && canEdit && (
            <Card className="mt-8 p-6 border-dashed border-2">
              <h3 className="font-medium mb-3">Missing Stages</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add these stages to complete your supply chain and earn +150 GSG:
              </p>
              <div className="flex flex-wrap gap-2">
                {summary.missing_stages.map(stageKey => {
                  const config = STAGE_CONFIG[stageKey];
                  const Icon = config?.icon || Package;
                  return (
                    <Badge key={stageKey} variant="outline" className="gap-1">
                      <Icon className="w-3 h-3" />
                      {config?.label || stageKey}
                    </Badge>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Info Card */}
          <Card className="mt-12 p-6 bg-primary/5 border-primary/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <Shield weight="duotone" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-2">Blockchain-Verified Traceability</h3>
                <p className="text-sm text-muted-foreground">
                  Each stage in the supply chain is recorded with a unique blockchain hash, 
                  ensuring immutable proof of the product's journey from raw materials to your hands.
                  Complete at least 3 stages to earn +150 GSG!
                </p>
              </div>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SupplyChainPage;
